import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CommentItem, { CommentShape } from '../CommentItem/CommentItem';
import CommentsModal from '../CommentsModal/CommentsModal';
import {
  DEFAULT_LABELS,
  DEFAULT_PREVIEW_COUNT,
  DEFAULT_MAX_LENGTH,
} from '../constants';
import { cx, sortByDateDesc } from '../utils';
import styles from './Comments.module.scss';

export default function Comments({
  comments,
  currentUserId: currentUserIdProp,
  previewCount,
  maxLength,
  loading,
  error,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onDownloadAttachment,
  getAttachmentUrl,
  isCommentEditable,
  confirmDelete,
  formatAuthor,
  formatDate,
  labels: labelsProp,
  allowAttachments,
  maxAttachments,
  acceptedFileTypes,
  className,
  modalClassName,
  renderEmpty,
  defaultOpen,
}) {
  const [isModalOpen, setModalOpen] = useState(defaultOpen);

  const storeWorkerId = useSelector(
    (state) => state.authentication?.userInfo?.WorkerId
  );
  const currentUserId = currentUserIdProp ?? storeWorkerId;

  const labels = useMemo(
    () => ({ ...DEFAULT_LABELS, ...labelsProp }),
    [labelsProp]
  );

  const sortedComments = useMemo(() => sortByDateDesc(comments), [comments]);
  const previewComments = useMemo(
    () => sortedComments.slice(-previewCount),
    [sortedComments, previewCount]
  );

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  return (
    <div className={cx(styles.root, className)}>
      <h3 className={styles.title}>{labels.title}</h3>

      {loading ? (
        <div className={styles.loading}>{labels.loading}</div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : previewComments.length === 0 ? (
        renderEmpty ? (
          renderEmpty()
        ) : (
          <div className={styles.empty}>{labels.noComments}</div>
        )
      ) : (
        <ul className={styles.list}>
          {previewComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              formatAuthor={formatAuthor}
              formatDate={formatDate}
              labels={labels}
              isCommentEditable={isCommentEditable}
              readOnly
              onDownloadAttachment={onDownloadAttachment}
              getAttachmentUrl={getAttachmentUrl}
            />
          ))}
        </ul>
      )}

      <button type="button" className={styles.openButton} onClick={openModal}>
        {sortedComments.length > 0
          ? labels.showAllButton(sortedComments.length)
          : labels.addButton}
      </button>

      {isModalOpen && (
        <CommentsModal
          className={modalClassName}
          comments={sortedComments}
          currentUserId={currentUserId}
          maxLength={maxLength}
          labels={labels}
          allowAttachments={allowAttachments}
          maxAttachments={maxAttachments}
          acceptedFileTypes={acceptedFileTypes}
          formatAuthor={formatAuthor}
          formatDate={formatDate}
          isCommentEditable={isCommentEditable}
          confirmDelete={confirmDelete}
          onAddComment={onAddComment}
          onEditComment={onEditComment}
          onDeleteComment={onDeleteComment}
          onDownloadAttachment={onDownloadAttachment}
          getAttachmentUrl={getAttachmentUrl}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

Comments.propTypes = {
  comments: PropTypes.arrayOf(CommentShape).isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  previewCount: PropTypes.number,
  maxLength: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onAddComment: PropTypes.func.isRequired,
  onEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  onDownloadAttachment: PropTypes.func,
  getAttachmentUrl: PropTypes.func,
  isCommentEditable: PropTypes.func,
  confirmDelete: PropTypes.func,
  formatAuthor: PropTypes.func,
  formatDate: PropTypes.func,
  labels: PropTypes.object,
  allowAttachments: PropTypes.bool,
  maxAttachments: PropTypes.number,
  acceptedFileTypes: PropTypes.string,
  className: PropTypes.string,
  modalClassName: PropTypes.string,
  renderEmpty: PropTypes.func,
  defaultOpen: PropTypes.bool,
};

Comments.defaultProps = {
  previewCount: DEFAULT_PREVIEW_COUNT,
  maxLength: DEFAULT_MAX_LENGTH,
  loading: false,
  error: '',
  allowAttachments: true,
  defaultOpen: false,
  comments: [],
};
